import { FlujoNoDisponible, type EventoDelFlujo } from './flujo';
import { NotificacionesNoDisponibles, type Cuenta } from './servicio';

/**
 * EL TIEMPO REAL DE LAS NOTIFICACIONES · 🆕 5/10/2026 (D15; ADR-0021).
 *
 * Mantiene al día la cuenta de la campana —sin leer, activas, por atender— y avisa
 * a quien se suscriba cada vez que cambia.
 *
 * ⚠️ **UNA CONEXIÓN POR NAVEGADOR, NO POR PESTAÑA.** Cada flujo ocupa uno de los
 * huecos de una instancia de la API, y quien trabaja con cinco pestañas —Operación y
 * Administración comparten el origen— gastaría cinco. Una de ellas es la **líder**
 * —la que tiene el cerrojo `vigilancia-notificaciones` de Web Locks— y sostiene el
 * flujo; las demás reciben la cuenta por `BroadcastChannel`. Si la líder se cierra,
 * el cerrojo pasa a otra. Sin Web Locks o sin `BroadcastChannel`, cada pestaña es
 * su propia líder.
 *
 * ⚠️ **ABIERTO MIENTRAS ALGUIEN MIRA.** Cada pestaña dice si se la ve; la líder
 * sostiene el flujo mientras alguna se vea —ella o cualquier otra— y lo cierra treinta
 * segundos después de que ninguna se vea: quien cambia de pestaña un momento no lo
 * cierra.
 *
 * ⚠️ **Y SI EL FLUJO NO ESTÁ, SE PREGUNTA COMO ANTES**: la cuenta cada minuto. El flujo
 * puede no estar porque la API no tiene sitio (503), porque su canal se cayó, porque
 * un balanceador retiene los eventos —**cuarenta segundos sin oír nada, ni el latido
 * de cada veinticinco**, y se da por retenido— o porque la red se cortó. Se vuelve a
 * intentar de treinta segundos a cinco minutos, doblando y con azar.
 *
 * ⚠️ **MANDA LO MÁS RECIENTE.** Cada cifra lleva desde cuándo vale —una pregunta, desde que
 * se hizo; un evento, desde que llegó— y viaja con ello por el canal: lo que llega tarde no
 * pisa lo más nuevo, ni en esta pestaña ni en las demás.
 *
 * Lo que dice el `fin` del servidor manda: **`renovar`** se reabre en el acto;
 * **`sesion`** —venció— calla las notificaciones, y lo descubrirá la pantalla en su
 * próxima petición. Un 401 en cualquier pregunta, lo mismo.
 *
 * **Modo `al-volver`** —el portal—: sin flujo y sin respaldo; la cuenta, al entrar y al
 * volver a la pestaña. Hoy casi nada le llega a un trabajador, y miles de flujos o de
 * preguntas por minuto serían carga sin nada detrás.
 */

/** Cada cuánto se pregunta la cuenta cuando no hay flujo: un minuto, como la primera vuelta. */
export const RESPALDO_MS = 60_000;
/** Sin oír nada del flujo en este tiempo —el latido es cada 25 s— se da por retenido. */
export const VIGIA_MS = 40_000;
/** La espera para volver a abrir el flujo tras un fallo: de treinta segundos a cinco minutos. */
export const PRIMERA_ESPERA_MS = 30_000;
export const ESPERA_MAXIMA_MS = 5 * 60_000;
/** Cuánto sigue abierto el flujo sin nadie mirando. */
export const GRACIA_MS = 30_000;
/** Cada cuánto dice una pestaña que se la ve. Sin noticias suyas en el doble, se da por ida. */
export const PRESENCIA_MS = 30_000;
/** Sin noticias de la líder en este tiempo, una pestaña que se ve pregunta ella misma. */
export const SILENCIO_DE_LA_LIDER_MS = 2 * RESPALDO_MS;
/**
 * 🆕 Cuánto por delante de la hora de esta pestaña puede decir valer una cifra sin ser «del
 * futuro». Dos pestañas no siempre leen la misma hora: en Chromium coinciden al milisegundo
 * —medido con cuatro—, pero un navegador puede redondearla por privacidad, y Firefox lo hace
 * a 16,7 ms o más si protege la huella. Un segundo lo cubre de sobra, y es también lo más
 * que la campana se quedaría quieta tras un salto atrás de la hora más corto que él.
 */
export const MARGEN_DEL_RELOJ_MS = 1_000;

export const NOMBRE_DEL_CERROJO = 'vigilancia-notificaciones';
export const NOMBRE_DEL_CANAL = 'vigilancia-notificaciones';

export type ModoDeTiempoReal = 'vivo' | 'al-volver';

/** Lo que el tiempo real usa de `BroadcastChannel`. */
export interface Canal {
  postMessage(mensaje: unknown): void;
  addEventListener(tipo: 'message', oyente: (evento: MessageEvent) => void): void;
  removeEventListener(tipo: 'message', oyente: (evento: MessageEvent) => void): void;
  close(): void;
}

/** Lo que el tiempo real usa de `navigator.locks`. */
export interface Cerrojos {
  request(
    nombre: string,
    opciones: { readonly signal: AbortSignal },
    alTenerlo: () => Promise<void>,
  ): Promise<unknown>;
}

export interface Reloj {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
  ahora(): number;
}

/** La pestaña: si se la ve, y avisar cuando cambia. */
export interface Pagina {
  visible(): boolean;
  /** Devuelve cómo dejar de escuchar. */
  alCambiar(oyente: () => void): () => void;
}

export interface DependenciasDelTiempoReal {
  readonly cuenta: () => Promise<Cuenta>;
  /** Abre y lee el flujo; resuelve al cerrarse, rechaza si no se pudo o se cortó. */
  readonly leerElFlujo: (
    senal: AbortSignal,
    alEvento: (evento: EventoDelFlujo) => void,
  ) => Promise<void>;
  readonly pagina: Pagina;
  readonly reloj: Reloj;
  readonly azar: () => number;
  readonly cerrojos?: Cerrojos | undefined;
  readonly canal?: (() => Canal) | undefined;
  readonly idDePestana: string;
}

export interface TiempoReal {
  /** La última cuenta conocida; `null` hasta la primera. */
  cuenta(): Cuenta | null;
  /** Cada cuenta nueva, con la de antes —para saber si subió—. Devuelve cómo darse de baja. */
  suscribir(oyente: (cuenta: Cuenta, antes: Cuenta | null) => void): () => void;
  /** Pregunta la cuenta ahora —tras un gesto propio— y se la dice a las demás pestañas. */
  refrescar(): Promise<void>;
  /**
   * La sesión caducó —lo vio esta pestaña o se lo dijo otra—: lo dice una vez a quien
   * escuche desde antes. Devuelve cómo darse de baja.
   */
  alCaducar(oyente: () => void): () => void;
  detener(): void;
}

/**
 * 🆕 Desde cuándo vale una cifra —en la hora del equipo, la que leen todas sus pestañas, con
 * el margen de `MARGEN_DEL_RELOJ_MS`— y en qué orden la supo esta pestaña, para los empates.
 */
interface Sello {
  readonly desde: number;
  readonly orden: number;
}

type Mensaje =
  /** `desde`: desde cuándo vale la cifra; `null` si no lo dice —una pestaña de antes—. */
  | { readonly tipo: 'cuenta'; readonly cuenta: Cuenta; readonly desde: number | null }
  | { readonly tipo: 'presente'; readonly pestana: string; readonly visible: boolean }
  | { readonly tipo: 'adios'; readonly pestana: string }
  | { readonly tipo: 'pregunta' }
  | { readonly tipo: 'vivo' }
  | { readonly tipo: 'callado' };

const esEntero = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** Una cuenta con su forma, venga del flujo o de otra pestaña; si no, nada. */
export function cuentaDe(crudo: unknown): Cuenta | null {
  if (typeof crudo !== 'object' || crudo === null) return null;
  const { sinLeer, activas, porAtender } = crudo as Record<string, unknown>;
  return esEntero(sinLeer) && esEntero(activas) && esEntero(porAtender)
    ? { sinLeer, activas, porAtender }
    : null;
}

const iguales = (a: Cuenta | null, b: Cuenta): boolean =>
  a !== null && a.sinLeer === b.sinLeer && a.activas === b.activas && a.porAtender === b.porAtender;

/** Lo que llega por el canal, con su forma; lo demás, fuera: el canal es de todo el origen. */
function mensajeDe(crudo: unknown): Mensaje | null {
  if (typeof crudo !== 'object' || crudo === null) return null;
  const m = crudo as Record<string, unknown>;
  const tipo = m['tipo'];
  const pestana = m['pestana'];
  switch (tipo) {
    case 'cuenta': {
      const cuenta = cuentaDe(m['cuenta']);
      const desde = m['desde'];
      if (cuenta === null) return null;
      // Sin «desde» —una pestaña de antes de que existiera— vale desde que llega, como entonces.
      if (desde === undefined) return { tipo, cuenta, desde: null };
      return typeof desde === 'number' && Number.isFinite(desde) ? { tipo, cuenta, desde } : null;
    }
    case 'presente': {
      const visible = m['visible'];
      return typeof pestana === 'string' && typeof visible === 'boolean'
        ? { tipo, pestana, visible }
        : null;
    }
    case 'adios':
      return typeof pestana === 'string' ? { tipo, pestana } : null;
    case 'pregunta':
    case 'vivo':
    case 'callado':
      return { tipo };
    default:
      return null;
  }
}

export function crearTiempoReal(
  dependencias: DependenciasDelTiempoReal,
  modo: ModoDeTiempoReal = 'vivo',
): TiempoReal {
  const { reloj, pagina } = dependencias;
  const suscriptores = new Set<(cuenta: Cuenta, antes: Cuenta | null) => void>();
  const alCaducar = new Set<() => void>();
  let ultima: Cuenta | null = null;
  let callado = false;
  let detenido = false;

  /* ── La cuenta ─────────────────────────────────────────────────────────── */

  /*
    🆕 5/10/2026 · MANDA LO MÁS RECIENTE. La cuenta la escriben el flujo —el de esta pestaña o,
    por el canal, el de la líder— y las preguntas por HTTP, de esta pestaña o de otra. Cada
    cifra lleva su sello, y con él viaja por el canal: una pregunta vale desde que se hizo; un
    evento del flujo, desde que llegó. Sólo escribe la que vale desde más tarde que lo
    escrito; si empatan, la que esta pestaña supo después.

    Sin esto, la respuesta de una pregunta hecha antes de un evento del flujo pisaba la cifra
    que el flujo acababa de traer, y la campana se quedaba con la vieja hasta el siguiente
    aviso. Y entre pestañas sin flujo, la ronda de la líder, hecha antes del gesto de otra
    pestaña, pisaba lo que el gesto dejó: en la líder o, por el canal, en la del gesto.

    Que un evento valga desde que LLEGA —cuándo salió del servidor no se sabe— es seguro: cada
    cambio le llega al flujo DESPUÉS de hacerse, así que lo que supiera de más una pregunta
    descartada lo trae él enseguida.
  */
  let ordenes = 0;
  let escrito: Sello = { desde: Number.NEGATIVE_INFINITY, orden: 0 };

  /** Lo que se empieza a saber: una pregunta, al hacerse; un evento o un mensaje, al llegar. */
  function sellar(desde: number): Sello {
    ordenes += 1;
    return { desde, orden: ordenes };
  }

  /**
   * Lo que dice valer desde el futuro —pasado el margen: se supo antes de que la hora del
   * equipo fuera hacia atrás, o viene mal dicho— vale menos que todo lo demás, y entre ello
   * decide el orden. Si no, lo escrito antes de un salto atrás no dejaría escribir nada hasta
   * que la hora lo alcanzara, y la campana se quedaría quieta.
   */
  function vigencia({ desde }: Sello, ahora: number): number {
    return desde > ahora + MARGEN_DEL_RELOJ_MS ? Number.NEGATIVE_INFINITY : desde;
  }

  function escribir(
    cuenta: Cuenta,
    sello: Sello,
    { difundir }: { readonly difundir: boolean },
  ): void {
    if (callado || detenido) return;
    const ahora = reloj.ahora();
    const desde = vigencia(sello, ahora);
    const desdeLoEscrito = vigencia(escrito, ahora);
    if (desde < desdeLoEscrito || (desde === desdeLoEscrito && sello.orden < escrito.orden)) {
      return;
    }
    escrito = sello;
    const antes = ultima;
    if (difundir) enviar({ tipo: 'cuenta', cuenta, desde: sello.desde });
    if (iguales(antes, cuenta)) return;
    ultima = cuenta;
    for (const oyente of [...suscriptores]) oyente(cuenta, antes);
  }

  /**
   * Pregunta la cuenta; un 401 calla, y cualquier otro fallo espera a la siguiente. La
   * respuesta vale desde que se hizo la pregunta, no desde que volvió.
   */
  async function preguntar(): Promise<void> {
    if (callado || detenido) return;
    const sello = sellar(reloj.ahora());
    try {
      escribir(await dependencias.cuenta(), sello, { difundir: true });
    } catch (error) {
      if (error instanceof NotificacionesNoDisponibles && error.motivo === 'sin-sesion') {
        callar({ difundir: true });
      }
    }
  }

  /**
   * La sesión venció: nada más se pregunta ni se abre, en ninguna pestaña. La que lo
   * descubre se lo dice a las demás; cada una lo dice a quien la escucha.
   */
  function callar({ difundir }: { readonly difundir: boolean }): void {
    if (callado) return;
    if (difundir) enviar({ tipo: 'callado' });
    callado = true;
    cerrarElFlujo();
    pararElRespaldo();
    cancelar(reintento);
    reintento = null;
    for (const oyente of [...alCaducar]) oyente();
  }

  /* ── El canal entre pestañas ───────────────────────────────────────────── */

  let canal: Canal | null = null;
  function enviar(mensaje: Mensaje): void {
    if (canal === null || detenido) return;
    try {
      canal.postMessage(mensaje);
    } catch {
      // Un canal cerrado no tumba nada: esta pestaña sigue por su cuenta.
    }
  }

  /** Cuándo vence la presencia de cada otra pestaña que se ve. */
  const presentes = new Map<string, number>();
  /** La última vez que se oyó a la líder: una cuenta o un «vivo». */
  let oidaLaLider = reloj.ahora();

  function alMensaje(evento: MessageEvent): void {
    const mensaje = mensajeDe(evento.data);
    if (mensaje === null || detenido) return;
    switch (mensaje.tipo) {
      case 'cuenta':
        oidaLaLider = reloj.ahora();
        escribir(mensaje.cuenta, sellar(mensaje.desde ?? reloj.ahora()), { difundir: false });
        break;
      case 'vivo':
        oidaLaLider = reloj.ahora();
        break;
      case 'presente':
        if (mensaje.visible) presentes.set(mensaje.pestana, reloj.ahora() + 2 * PRESENCIA_MS);
        else presentes.delete(mensaje.pestana);
        evaluar();
        break;
      case 'adios':
        presentes.delete(mensaje.pestana);
        evaluar();
        break;
      case 'pregunta':
        if (lider && ultima !== null) {
          enviar({ tipo: 'cuenta', cuenta: ultima, desde: escrito.desde });
        }
        break;
      case 'callado':
        callar({ difundir: false });
        break;
    }
  }

  /* ── La líder: el flujo, el vigía y el respaldo ────────────────────────── */

  let lider = false;
  let flujo: AbortController | null = null;
  let vigia: unknown = null;
  let cierre: unknown = null;
  let reintento: unknown = null;
  let respaldo: unknown = null;
  let fallos = 0;

  function cancelar(id: unknown): void {
    if (id !== null) reloj.clearTimeout(id);
  }

  function alguienMira(): boolean {
    if (pagina.visible()) return true;
    const ahora = reloj.ahora();
    for (const [pestana, vence] of presentes) {
      if (vence > ahora) return true;
      presentes.delete(pestana);
    }
    return false;
  }

  /** Lo que tiene que estar abierto según quién mira. Se llama ante cualquier cambio. */
  function evaluar(): void {
    if (!lider || callado || detenido) return;
    if (alguienMira()) {
      cancelar(cierre);
      cierre = null;
      if (flujo === null && reintento === null) abrir();
      if (flujo === null) asegurarElRespaldo();
      return;
    }
    // Nadie mira: el respaldo no pregunta, y el flujo se cierra tras la gracia.
    pararElRespaldo();
    if (flujo !== null && cierre === null) {
      cierre = reloj.setTimeout(() => {
        cierre = null;
        if (!alguienMira()) cerrarElFlujo();
      }, GRACIA_MS);
    }
  }

  function armarElVigia(): void {
    cancelar(vigia);
    vigia = reloj.setTimeout(() => {
      vigia = null;
      // Retenido: se cierra, y se pregunta como antes hasta que vuelva.
      cerrarElFlujo();
      caer();
    }, VIGIA_MS);
  }

  function cerrarElFlujo(): void {
    cancelar(vigia);
    vigia = null;
    cancelar(cierre);
    cierre = null;
    const abierto = flujo;
    flujo = null;
    abierto?.abort();
  }

  function abrir(): void {
    if (callado || detenido || flujo !== null) return;
    const control = new AbortController();
    flujo = control;
    let motivo: string | null = null;
    armarElVigia();
    const alEvento = (evento: EventoDelFlujo): void => {
      if (flujo !== control) return;
      armarElVigia();
      if (evento.tipo === 'latido') {
        enviar({ tipo: 'vivo' });
        return;
      }
      let datos: unknown;
      try {
        datos = JSON.parse(evento.datos);
      } catch {
        return;
      }
      if (evento.tipo === 'cuenta') {
        const cuenta = cuentaDe(datos);
        if (cuenta === null) return;
        fallos = 0;
        pararElRespaldo();
        escribir(cuenta, sellar(reloj.ahora()), { difundir: true });
      } else if (evento.tipo === 'fin') {
        const dicho = (datos as Record<string, unknown> | null)?.['motivo'];
        motivo = typeof dicho === 'string' ? dicho : null;
      }
    };
    dependencias.leerElFlujo(control.signal, alEvento).then(
      () => {
        if (flujo !== control) return;
        flujo = null;
        cancelar(vigia);
        vigia = null;
        if (motivo === 'sesion') {
          callar({ difundir: true });
        } else if (motivo === 'renovar') {
          evaluar();
        } else {
          // `canal`, `apagado`, `error`, o un cierre sin `fin`.
          caer();
        }
      },
      (error: unknown) => {
        if (flujo !== control) return;
        flujo = null;
        cancelar(vigia);
        vigia = null;
        if (error instanceof FlujoNoDisponible && error.motivo === 'sin-sesion') {
          callar({ difundir: true });
        } else {
          caer();
        }
      },
    );
  }

  /** Sin flujo: se pregunta como antes y se vuelve a intentar más tarde. */
  function caer(): void {
    if (callado || detenido) return;
    fallos += 1;
    asegurarElRespaldo();
    cancelar(reintento);
    const espera = Math.min(PRIMERA_ESPERA_MS * 2 ** (fallos - 1), ESPERA_MAXIMA_MS);
    reintento = reloj.setTimeout(
      () => {
        reintento = null;
        evaluar();
      },
      Math.round(espera * (0.8 + dependencias.azar() * 0.4)),
    );
  }

  function asegurarElRespaldo(): void {
    if (respaldo !== null || callado || detenido) return;
    const vuelta = (): void => {
      respaldo = reloj.setTimeout(vuelta, RESPALDO_MS);
      if (alguienMira()) void preguntar();
    };
    vuelta();
  }

  function pararElRespaldo(): void {
    cancelar(respaldo);
    respaldo = null;
  }

  /* ── Las seguidoras: si la líder calla, preguntan ellas ────────────────── */

  let presencia: unknown = null;
  function anunciarse(): void {
    cancelar(presencia);
    presencia = null;
    if (detenido) return;
    const visible = pagina.visible();
    enviar({ tipo: 'presente', pestana: dependencias.idDePestana, visible });
    if (!visible) return;
    presencia = reloj.setTimeout(() => {
      presencia = null;
      anunciarse();
      // Seguidora que se ve y lleva demasiado sin oír a la líder: pregunta ella.
      if (!lider && reloj.ahora() - oidaLaLider >= SILENCIO_DE_LA_LIDER_MS) void preguntar();
    }, PRESENCIA_MS);
  }

  /* ── Arranque ──────────────────────────────────────────────────────────── */

  const soltarLaVisibilidad = pagina.alCambiar(() => {
    if (detenido) return;
    if (modo === 'al-volver') {
      if (pagina.visible()) void preguntar();
      return;
    }
    anunciarse();
    evaluar();
  });

  let soltarElCerrojo: (() => void) | null = null;
  const dejarDeEsperar = new AbortController();

  if (modo === 'al-volver') {
    void preguntar();
  } else {
    if (dependencias.canal !== undefined && dependencias.cerrojos !== undefined) {
      try {
        canal = dependencias.canal();
        canal.addEventListener('message', alMensaje);
      } catch {
        canal = null;
      }
    }
    if (canal !== null && dependencias.cerrojos !== undefined) {
      enviar({ tipo: 'pregunta' });
      anunciarse();
      dependencias.cerrojos
        .request(NOMBRE_DEL_CERROJO, { signal: dejarDeEsperar.signal }, () => {
          if (detenido) return Promise.resolve();
          lider = true;
          // La cuenta de ahora, sin esperar al flujo: si tardara en abrirse, la
          // campana estaría hasta el vigía sin número.
          void preguntar();
          evaluar();
          // Se queda con el cerrojo hasta que se detenga: entonces pasa a otra.
          return new Promise<void>((soltar) => {
            soltarElCerrojo = soltar;
          });
        })
        .catch(() => {
          // Detenida antes de tenerlo: nada que soltar.
        });
    } else {
      // Sin cerrojos o sin canal, cada pestaña es su propia líder.
      lider = true;
      void preguntar();
      evaluar();
    }
  }

  return {
    cuenta: () => ultima,
    suscribir(oyente) {
      suscriptores.add(oyente);
      return () => {
        suscriptores.delete(oyente);
      };
    },
    refrescar: preguntar,
    alCaducar(oyente) {
      alCaducar.add(oyente);
      return () => {
        alCaducar.delete(oyente);
      };
    },
    detener() {
      if (detenido) return;
      enviar({ tipo: 'adios', pestana: dependencias.idDePestana });
      detenido = true;
      soltarLaVisibilidad();
      cerrarElFlujo();
      pararElRespaldo();
      cancelar(reintento);
      cancelar(presencia);
      reintento = null;
      presencia = null;
      lider = false;
      dejarDeEsperar.abort();
      const soltar = soltarElCerrojo;
      soltarElCerrojo = null;
      soltar?.();
      if (canal !== null) {
        canal.removeEventListener('message', alMensaje);
        canal.close();
        canal = null;
      }
      suscriptores.clear();
      alCaducar.clear();
    },
  };
}
